import { useEffect, useRef, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { API_BASE_URL, readGoogleConsent, type SmbGoogleConsent } from '../lib/api'
import { SmbGoogleAllowStep } from './SmbGoogleAllowStep'

export const CONSENT_TIMEOUT_MS = 15000

// DW-ITEM2-SMB-ALLOW-AFTER-MEETING — the customer-path bridge to the Allow
// step. SmbVerifyPage persists the SMB session token on verify success; this
// card, mounted on the client home ONLY behind the interview gate (completed
// first meeting), redeems it against the existing consent endpoint and offers
// one Continue button.
//
// Doctrine:
// - No token in storage → render NOTHING. This card only exists for a
//   principal mid-onboarding; everyone else's home stays untouched.
// - The consent POST is unauthenticated by design (same as verify) — plain
//   fetch, never the staff axios client.
// - The returned consent_url is only honoured when it points at
//   https://login.microsoftonline.com/ — anything else renders NOTHING
//   (never navigate off-allow on a bad payload).
// - Failed fetch / non-2xx / throw → render NOTHING. Unknown stays unknown.
// - Visible copy never prints the URL or the session token, and never says
//   we are watching / we detected / Microsoft / 365 / Allow / consent. The
//   button is Continue; the body says grant access.
// - The token stays in storage after a 200 (backend is idempotent for
//   consent_sent) so a refresh can re-request; the callback landing handles
//   the return leg.
// - BRIEF-B6b: a Google Workspace session's consent body carries cloud
//   'google_workspace' with client_id, scopes and admin_console_url instead
//   of a consent_url. That body (and only a well-formed one, console link on
//   admin.google.com) hands over to SmbGoogleAllowStep. The Microsoft rule
//   above is unchanged: a body without that cloud still needs a
//   login.microsoftonline.com URL or renders nothing.
// - onSettled (optional) is called exactly once, when the step knows whether
//   it will render: true with a card (Microsoft or Google), false for every
//   path that renders nothing. A host page (SmbVerifyPage) holds its own
//   call to action back until then, so nothing flashes while consent loads.
export function SmbAllowStep({
  onSettled,
}: { onSettled?: (willRender: boolean) => void } = {}) {
  const [consentUrl, setConsentUrl] = useState<string | null>(null)
  const [google, setGoogle] = useState<
    { session: string; grant: SmbGoogleConsent } | null
  >(null)
  // Effects can re-fire (StrictMode remount); the consent request runs once.
  const startedRef = useRef(false)
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled
  const settledRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const settle = (willRender: boolean) => {
      if (settledRef.current) return
      settledRef.current = true
      onSettledRef.current?.(willRender)
    }

    let token: string | null = null
    try {
      token = sessionStorage.getItem('dw_smb_session')
    } catch {
      // Storage unavailable → no claim to make.
      settle(false)
      return
    }
    if (!token) {
      settle(false)
      return
    }

    void (async (sessionToken: string) => {
      // A consent request that never answers must not leave the page waiting
      // for ever: after 15 seconds it is abandoned and the page falls back to
      // its sign-in line (app polish review).
      const ctrl = new AbortController()
      const timer = setTimeout(() => ctrl.abort(), CONSENT_TIMEOUT_MS)
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/v1/smb-onboarding/${encodeURIComponent(sessionToken)}/consent`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: ctrl.signal,
          },
        )
        if (!res.ok) {
          settle(false)
          return
        }
        const body: unknown = await res.json()
        if ((body as { cloud?: unknown } | null)?.cloud === 'google_workspace') {
          const grant = readGoogleConsent(body)
          if (grant) setGoogle({ session: sessionToken, grant })
          settle(grant !== null)
          return
        }
        const url = (body as { consent_url?: unknown } | null)?.consent_url
        if (
          typeof url !== 'string' ||
          !url.startsWith('https://login.microsoftonline.com/')
        ) {
          settle(false)
          return
        }
        setConsentUrl(url)
        settle(true)
      } catch {
        // Stay silent: unknown stays unknown, nothing renders.
        settle(false)
      } finally {
        clearTimeout(timer)
      }
    })(token)
  }, [])

  if (google) {
    return <SmbGoogleAllowStep session={google.session} grant={google.grant} />
  }
  if (!consentUrl) return null

  return (
    <section
      aria-label="organisation access"
      className="mb-6 bg-white rounded-xl border border-brass/40 p-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4 text-brass" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-navy">One more step</h2>
      </div>
      <p className="text-sm text-gray-500 mb-3">
        Your organisation still needs to grant access. Nothing is connected
        yet.
      </p>
      <button
        type="button"
        onClick={() => window.location.assign(consentUrl)}
        className="tap-min px-5 py-2.5 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brass focus:ring-offset-2"
      >
        Continue
      </button>
    </section>
  )
}
